import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useSettings } from '../contexts/SettingsContext';
import { buildWebSocketUrl } from '../utils/websocket';
import { isHybridMode } from '../utils/config';
import { playSound } from '../utils/sounds';
import { publishQueueEvent } from '../utils/queueEventBus';

/**
 * Single global WebSocket for queue/match events.
 *
 * Mounted once at App.js level. Plays match-found sounds, navigates on
 * lifecycle events, and fans every event out via the queue event bus so
 * other components (Queue.jsx, AcceptMatchBanner) can listen without
 * opening duplicate connections.
 *
 * @param {string|null} guildId - Guild ID for the WebSocket URL.
 * @param {object} handlers - Optional callbacks: onReadyCheck,
 *   onReadyCheckFinalized, onMatchFound, onAcceptUpdate, onMatchCancelled,
 *   onAllAccepted, onMatchCreatedPendingAccept, onMatchAcceptUpdate,
 *   onAnyMatchCancelled.
 */
export default function useQueueWebSocket(guildId, handlers = {}) {
  const {
    onReadyCheck,
    onReadyCheckFinalized,
    onMatchFound,
    onAcceptUpdate,
    onMatchCancelled,
    onAllAccepted,
    onMatchCreatedPendingAccept,
    onMatchAcceptUpdate,
    onAnyMatchCancelled,
  } = handlers;
  const { user, isLoggedIn } = useAuth();
  const { addNotification } = useNotifications();
  const { settings, shouldNotify } = useSettings();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const shouldNotifyRef = useRef(shouldNotify);
  shouldNotifyRef.current = shouldNotify;
  // t via ref so the WS effect (deps below) doesn't tear down on language change.
  const tRef = useRef(t);
  tRef.current = t;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!isHybridMode || !guildId || !isLoggedIn) return;

    let alive = true;
    let reconnectTimeout = null;
    let heartbeat = null;

    /**
     * Match-events bär ett additivt ``player_pks``-fält (Player.pk) bredvid
     * legacy ``player_ids`` (discord-id-strängar). Helpern föredrar player_pks
     * (Sprint 5 player_id-cutover, både API och bot publicerar nu) och faller
     * tillbaka på player_ids — steam-only users (utan discord_id) omfattas
     * via pk-vägen, gamla bot-versioner utan player_pks via discord-id-vägen.
     * Fallbacken kan tas bort när alla bot-instanser är migrerade.
     *
     * @param {object} d - WS-payload
     * @returns {boolean} true om inloggad användare är med i matchen
     */
    const includesMe = (d) => {
      const myPk = user?.player_id ? String(user.player_id) : null;
      const myDiscord = user?.discord_id ? String(user.discord_id) : null;
      if (myPk && d.player_pks?.some(p => String(p) === myPk)) return true;
      if (myDiscord && d.player_ids?.some(p => String(p) === myDiscord)) return true;
      return false;
    };

    const onMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // match_events channel is global — drop events from other guilds.
        // queue_update:{guild_id} is already server-side filtered.
        if (data.event && data.guild_id && String(data.guild_id) !== String(guildId)) {
          return;
        }

        // Fan out to subscribers (Queue.jsx, etc.) before doing local work
        // so they always see every event, even ones we navigate on.
        publishQueueEvent(data);

        if (data.action === 'match_found' && includesMe(data)) {
          playSound(settingsRef.current.matchFoundSound, settingsRef.current.soundVolume);
          // No toast — AcceptMatchBanner now renders globally on every route,
          // so a "click to accept" toast would just navigate to /queue after
          // the user has already accepted/declined via the modal.
          handlersRef.current.onMatchFound?.(data);
        }
        if (data.action === 'accept_update') {
          handlersRef.current.onAcceptUpdate?.(data);
        }
        if (data.action === 'match_cancelled') {
          handlersRef.current.onMatchCancelled?.(data);
        }
        if (data.action === 'all_accepted') {
          handlersRef.current.onAllAccepted?.(data);
        }
        if (data.event === 'match_ended' && includesMe(data)) {
          if (shouldNotifyRef.current('match_ended')) {
            addNotification({
              message: tRef.current('notify.matchEnded'),
              type: 'match',
              action: () => navigate(`/stats/matches/${data.match_id}`),
            });
          }
          navigate(`/stats/matches/${data.match_id}`);
        }
        if (data.event === 'ready_check_started' && includesMe(data)) {
          handlersRef.current.onReadyCheck?.({
            match_id: data.match_id,
            timeout: data.timeout || 90,
          });
        }
        if (data.event === 'ready_check_finalized') {
          handlersRef.current.onReadyCheckFinalized?.();
        }
        if (data.event === 'veto_started' && includesMe(data) && data.created_by !== user?.discord_id) {
          if (shouldNotifyRef.current('veto_started')) {
            addNotification({
              message: tRef.current('notify.vetoStarted'),
              type: 'veto',
              duration: 30000,
              action: () => navigate(`/veto/${data.match_id}`),
            });
          }
          navigate(`/veto/${data.match_id}`);
        }
        if (data.event === 'match_created' && includesMe(data)) {
          const isCreator = String(data.created_by || '') === String(user?.discord_id || '');
          // Queue-originated matches already played the match-found sound and
          // showed an accept modal at that point; a second sound + toast here
          // is redundant. For /match create (from_queue missing) we still
          // notify non-creators since that's their first signal.
          if (!isCreator && !data.from_queue) {
            playSound(settingsRef.current.matchFoundSound, settingsRef.current.soundVolume);
            if (shouldNotifyRef.current('match_created')) {
              addNotification({
                message: tRef.current('notify.matchCreated'),
                type: 'match',
                action: () => navigate(`/lobby/${data.match_id}`),
              });
            }
          }
          // When pending_accept is set, defer navigation to the global
          // accept modal in App.js (it navigates on all_accepted). Creator
          // also gets the modal — they're auto-accepted server-side, so it
          // just shows "väntar på andra" until everyone else responds.
          if (data.pending_accept) {
            handlersRef.current.onMatchCreatedPendingAccept?.(data);
          } else if (!isCreator) {
            navigate(`/lobby/${data.match_id}`);
          }
        }
        if (data.event === 'match_accept_update') {
          handlersRef.current.onMatchAcceptUpdate?.(data);
        }
        if (data.event === 'match_launched' && data.connect_string) {
          if (shouldNotifyRef.current('match_launched')) {
            addNotification({
              message: tRef.current('notify.serverReady', { connect: data.connect_string }),
              type: 'match',
              duration: 60000,
            });
          }
        }
        if (data.event === 'match_cancelled') {
          // Suppress the toast only when the cancellor is THIS user (they
          // clicked Cancel themselves). Auto-decline / queue-timeout sends
          // varken cancelled_by_pk eller cancelled_by — every user gets the toast.
          //
          // Föredra cancelled_by_pk (voice-agnostiskt, Player.pk) över
          // cancelled_by (legacy discord_id). Båda jämförs så att gamla
          // publishers fortfarande kan tysta toast hos cancellern.
          const isCancellorByPk = data.cancelled_by_pk && user?.player_id
            && String(data.cancelled_by_pk) === String(user.player_id);
          const isCancellorByDiscord = data.cancelled_by && user?.discord_id
            && String(data.cancelled_by) === String(user.discord_id);
          const isCancellor = isCancellorByPk || isCancellorByDiscord;
          if (!isCancellor && shouldNotifyRef.current('match_cancelled')) {
            addNotification({
              message: tRef.current('notify.matchCancelled'),
              type: 'warning',
            });
          }
          const path = window.location.pathname;
          if (path.includes(`/lobby/${data.match_id}`) || path.includes(`/veto/${data.match_id}`)) {
            navigate('/');
          }
          handlersRef.current.onAnyMatchCancelled?.(data);
        }
        if (data.event === 'match_creation_failed' && includesMe(data)) {
          addNotification({
            message: tRef.current('notify.matchCreationFailed', { error: data.error || tRef.current('notify.unknownError') }),
            type: 'warning',
            duration: 15000,
          });
        }
      } catch (e) {}
    };

    const connect = () => {
      const wsUrl = buildWebSocketUrl(`/api/queue/ws/${guildId}`);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = onMessage;
      ws.onclose = () => {
        if (alive) reconnectTimeout = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };

    connect();

    heartbeat = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30000);

    return () => {
      alive = false;
      clearInterval(heartbeat);
      clearTimeout(reconnectTimeout);
      wsRef.current?.close();
    };
    // handlersRef captures latest handlers — re-running on every change
    // would tear down the WS unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildId, isLoggedIn, user, navigate, addNotification]);

  return wsRef;
}
